import type {
  PaginationState,
  SortingState,
  Header,
  RowSelectionState,
  Row,
  ExpandedState,
} from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  getGroupedRowModel,
} from "@tanstack/react-table";
import { FALLBACK_VALUE } from "common/constants";
import type { Dispatch, HTMLAttributes, ReactNode, SetStateAction } from "react";
import { useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import cx from "ui/cx";
import useWindowSize from "ui/hooks/useWindowSize";
import IconInfoLine from "ui/icons/IconInfoLine";
import IconSort from "ui/icons/IconSort";
import IconTrash from "ui/icons/IconTrash";
import { wrapWithTooltip } from "ui/popovers/Tooltip/withTooltip";
import EmptySearchResults from "ui/search/EmptySearchResults";
import { generateColDefs, getColumnId } from "./column";
import ColumnHeaderMenu from "./ColumnHeaderMenu";
import {
  DRAGGABLE_CELL_WIDTH,
  EXPAND_CELL_WIDTH,
  OVERFLOW_MENU_CELL_WIDTH,
  MULTI_SELECT_CELL_WIDTH,
  SUB_TABLE_PADDING_RIGHT,
  DATA_TABLE_DEFAULT_PAGE_SIZE,
  MOBILE_WIDTH,
  DEFAULT_MIN_STARTING_WIDTH,
  DEFAULT_PAGINATION_STATE,
  MAXIMIZE_COLUMN_BUFFER,
  PAGE_SIZES,
} from "./constants";
import Controls from "./Controls";
import styles from "./DataTable.module.scss";
import DataTableLoading from "./DataTableLoading";
import { MultiSelectMenu, MultiSelectHeader } from "./Multiselect";
import { Pagination, shouldShowCursorPagination } from "./Pagination";
import ShowHideColumns from "./ShowHideColumns";
import TableRow from "./TableRow";
import type { ClientPaginationState } from "./types";
import type { DataTableProps, DataTableColumn, OrderByArgs } from "./types";
import { useColumnSizing } from "./useColumnSizing";

/**
 * The unclipped width an element wants: its own content (scroll) width plus any text hidden inside
 * descendant form fields, which scroll their value internally rather than stretching the layout.
 */
function intrinsicContentWidth(el: HTMLElement): number {
  let width = el.scrollWidth;
  el.querySelectorAll<HTMLElement>("input, textarea").forEach((field) => {
    width += Math.max(0, field.scrollWidth - field.clientWidth);
  });
  return width;
}

/**
 * How many extra pixels a single cell needs to fully reveal its (possibly clipped) content: the
 * summed intrinsic width of its in-flow children plus flex gaps between them, minus the available
 * content-box width. Uses live DOM measurement so it accounts for icons, bold text, inputs, etc.
 * without any font math. Negative when the cell already has slack.
 */
function measureCellContentDelta(cell: HTMLElement): number {
  const cellStyle = getComputedStyle(cell);
  const available = cell.clientWidth - parseFloat(cellStyle.paddingLeft) - parseFloat(cellStyle.paddingRight);
  // A cell can lay several elements side by side (e.g. an icon beside the text, or a "$" prefix next
  // to an input), so sum every in-flow child's intrinsic width. Absolutely-positioned overlays (the
  // resize handle and header caret) sit on top of the content and take no column space, so skip them.
  let needed = 0;
  let inFlowCount = 0;
  for (const child of Array.from(cell.children) as HTMLElement[]) {
    const childStyle = getComputedStyle(child);
    if (childStyle.position === "absolute") {
      continue;
    }
    needed += intrinsicContentWidth(child) + parseFloat(childStyle.marginLeft) + parseFloat(childStyle.marginRight);
    inFlowCount++;
  }
  // Flex column-gap (e.g. `.cell.icon { gap: 5px }`) is space between children that no child's
  // scrollWidth or margin captures. `columnGap` is "normal" when unset -> parseFloat NaN, so `|| 0`.
  needed += (parseFloat(cellStyle.columnGap) || 0) * Math.max(0, inFlowCount - 1);
  return needed - available;
}

/**
 * See https://tanstack.com/table/v8/docs/introduction for documentation
 */

export default function DataTable<T extends {}, P extends {}>({
  columns,
  data,
  onRowClick,
  onRowMove,
  onRowDrop,
  getRowClassNames,
  isRowHighlighted,
  isRowSelected,
  onRowMouseEnter,
  onRowMouseLeave,
  getRowCanExpand,
  getRowCanClick = () => true,
  subTable,
  parentTableWidth,
  parent,
  expandableRow,
  grouping: externalGrouping,
  overflowMenu,
  multiSelect,
  draggable = false,
  rowGaps = false,
  align = "left",
  cellBorders = false,
  fixedHeader = false,
  fixedFooter = false,
  paginationMethod = "offset",
  stickyPagination = false,
  pageSizes = PAGE_SIZES,
  size = "small",
  zebra = false,
  showColumnHeaders = true,
  showHideColumns,
  showHideColumnsStorageKey,
  handleColumnVisibilityChange,
  getRowHref,
  controls,
  className,
  rowStateClassNames,
  scrollIntoView,
  enableRowSelection,
  enableColumnResizing = true,
  fadeIn,
  onEdit,
  onAdd,
  getTableAPI,
  emptyCTA,
  emptyCTAContainerClassName,
  pagination: externalPagination,
  setPagination: setExternalPagination,
  autoResetPageIndex: autoResetPageIndexProp = true,
  defaultPageSize = DATA_TABLE_DEFAULT_PAGE_SIZE,
  totalCount,
  horizontalOverflow,
  minStartingColumnWidth = DEFAULT_MIN_STARTING_WIDTH,
  columnFilters = [],
  tableInfo,
  rowIdColumn,
  getRowId,
  expandedRowState: externalExpanded,
  onExpandedRowStateChange: setExternalExpanded,
  orderBy: externalOrderBy,
  setOrderBy: setExternalOrderBy,
  rowSelection: externalRowSelection,
  onRowSelectionChange: setExternalRowSelection,
  loading,
  dataSetKey,
  ...props
}: DataTableProps<T, P>) {
  const windowSize = useWindowSize();
  const isMobileView = windowSize[1] <= MOBILE_WIDTH;

  const refState = useRef<{ lastSelectedRow?: number }>({});
  const ref = useRef<HTMLDivElement>(null);
  // Holds the multi-select menu node (when shown) so the sticky header can offset below it.
  const [multiSelectNode, setMultiSelectNode] = useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) {
      return;
    }

    if (!multiSelectNode) {
      root.style.setProperty("--table-multiselect-height", "0px");
      return;
    }

    const updateHeight = () =>
      root.style.setProperty("--table-multiselect-height", `${multiSelectNode.offsetHeight}px`);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(multiSelectNode);
    return () => observer.disconnect();
  }, [multiSelectNode]);

  const [rowSelectionInternal, setRowSelectionInternal] = useState<RowSelectionState>({});
  const [sortingState, setSortingState] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const deferredFilter = useDeferredValue(globalFilter);
  const [records, setRecords] = useState(data || []);
  const [isReady, setIsReady] = useState(!fadeIn);
  const [paginationInternal, setPaginationInternal] = useState<ClientPaginationState>(
    externalPagination ?? { ...DEFAULT_PAGINATION_STATE, pageSize: defaultPageSize },
  ) satisfies [PaginationState, Dispatch<SetStateAction<PaginationState>>];

  const [expandedInternal, setExpandedInternal] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(columns.map((c) => [getColumnId(c), !c.hide])),
  );
  useEffect(() => {
    handleColumnVisibilityChange?.(columnVisibility);
  }, [columnVisibility]);

  // Measured container width for column sizing - only used when not a nested table
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);
  const measuredWidthRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    // Skip measuring for nested tables (they use parentTableWidth) or when resizing is disabled
    if (parentTableWidth || !enableColumnResizing || isMobileView) {
      return;
    }
    const element = ref.current;
    if (!element) {
      return;
    }

    // Use ResizeObserver to track container width changes
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const newWidth = entry.contentRect.width;
        const prevWidth = measuredWidthRef.current;
        // Ignore small width changes (e.g., scrollbar appearing ~15-20px) to prevent unnecessary re-renders
        if (prevWidth === null || Math.abs(newWidth - prevWidth) > 25) {
          measuredWidthRef.current = newWidth;
          setMeasuredWidth(newWidth);
        }
      }
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [parentTableWidth, enableColumnResizing, isMobileView]);

  const tableRef = useRef<ReturnType<typeof useReactTable<T>> | null>(null);

  const useServerSideSorting = !!setExternalOrderBy;

  const internalSortingState = useMemo(() => {
    if (externalOrderBy && Object.keys(externalOrderBy).length > 0) {
      const [id, direction] = Object.entries(externalOrderBy)[0];
      return [{ id, desc: direction === "desc" }];
    }
    return sortingState;
  }, [externalOrderBy, sortingState]);

  const handleSortingChange = useCallback(
    (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updaterOrValue === "function" ? updaterOrValue(internalSortingState) : updaterOrValue;

      if (useServerSideSorting && setExternalOrderBy) {
        // Convert SortingState to OrderBy format
        if (newSorting.length > 0) {
          const { id, desc } = newSorting[0];
          setExternalOrderBy({ [id]: desc ? "desc" : "asc" } as OrderByArgs<T>);
        } else {
          setExternalOrderBy(undefined);
        }
      } else {
        setSortingState(newSorting);
      }
    },
    [internalSortingState, useServerSideSorting, setExternalOrderBy, setSortingState],
  );

  useEffect(() => {
    setColumnVisibility((prev) => computeColumnVisibility(columns, prev));
  }, [columns]);

  const visibleColumnIds = useMemo(
    () => columns.filter((c) => columnVisibility[getColumnId(c)] !== false).map((c) => getColumnId(c)),
    [columnVisibility, columns],
  );
  const visibleColumnCount = visibleColumnIds.length;

  const columnLeadSeparatorIds = useMemo(() => {
    const ids = new Set<string>();
    for (const col of columns) {
      if (col.columnLeadSeparator) {
        ids.add(getColumnId(col));
      }
    }
    return ids;
  }, [columns]);

  useEffect(() => {
    setRecords(data || []);
    if (!externalPagination && autoResetPageIndexProp) {
      // Reset the page index when the data changes
      setPaginationInternal((prev) => ({ ...prev, pageIndex: DEFAULT_PAGINATION_STATE.pageIndex }));

      // Reset the row selection
      setRowSelectionInternal({});
    }
  }, [data]);

  useEffect(() => {
    // The dataSet has changed (not just pagination), reset the row selection
    if (dataSetKey) {
      // Reset the row selection
      setRowSelectionInternal({});
    }
  }, [dataSetKey]);

  useEffect(() => {
    // If pagination is controlled from parent, update it
    if (externalPagination) {
      setPaginationInternal(externalPagination);
    }
  }, [externalPagination?.pageIndex, externalPagination?.pageSize, externalPagination?.isLoading]);

  useEffect(() => {
    // If row selection is controlled from parent, update it
    if (externalRowSelection) {
      setRowSelectionInternal(externalRowSelection);
    }
  }, [externalRowSelection]);

  useEffect(() => {
    if (fadeIn) {
      setIsReady(true);
    }
  }, [fadeIn]);

  useEffect(() => {
    // if search input is handled externally, sync filter state
    if (!controls?.search) {
      setGlobalFilter(controls?.searchValue ?? "");
    }
  }, [controls?.searchValue, controls?.search]);

  const addNewRow = useCallback(
    (emptyRow: Partial<T>) => {
      if (controls?.addNew) {
        setRecords([emptyRow as T, ...records]);
      }
    },
    [controls?.addNew, records],
  );

  const hasGrouping = externalGrouping !== undefined && externalGrouping.length > 0;
  const expandable = !!expandableRow || !!subTable || !!hasGrouping;

  // https://jpcamara.com/2023/03/07/making-tanstack-table.html
  // https://github.com/TanStack/table/pull/4495
  // https://github.com/TanStack/table/issues/4330#issuecomment-1276865766
  const grouping = useMemo(
    () => (hasGrouping ? externalGrouping.map(String) : undefined),
    [hasGrouping, externalGrouping],
  );

  const tableWidth = useMemo(() => {
    // For nested tables, use parentTableWidth; otherwise use measured container width
    let tableWidth: number | null = null;

    if (parentTableWidth) {
      // Nested table: use parent's width minus padding
      tableWidth = parentTableWidth - SUB_TABLE_PADDING_RIGHT;
    } else if (measuredWidth && enableColumnResizing && !isMobileView) {
      // Top-level table: use measured container width
      tableWidth = measuredWidth;
    }

    if (tableWidth) {
      // Subtract fixed cell widths (must match SCSS values)
      tableWidth -= draggable ? DRAGGABLE_CELL_WIDTH : 0;
      tableWidth -= expandable ? EXPAND_CELL_WIDTH : 0;
      tableWidth -= overflowMenu || showHideColumns ? OVERFLOW_MENU_CELL_WIDTH : 0;
      tableWidth -= multiSelect ? MULTI_SELECT_CELL_WIDTH : 0;

      if (horizontalOverflow) {
        // Set lower bound for width so columns can push out of the container
        const minWidth = visibleColumnCount * minStartingColumnWidth;
        tableWidth = Math.max(minWidth, tableWidth);
      }
    }
    return tableWidth;
  }, [
    visibleColumnCount,
    draggable,
    expandable,
    horizontalOverflow,
    isMobileView,
    multiSelect,
    overflowMenu,
    showHideColumns,
    parentTableWidth,
    measuredWidth,
    enableColumnResizing,
    minStartingColumnWidth,
  ]);

  const getTable = useCallback(() => tableRef.current, []);
  const { columnSizing, handleColumnSizingChange, maximizeColumn, maximizeColumns } = useColumnSizing({
    tableWidth,
    columnVisibility,
    visibleColumnIds,
    getTable,
    horizontalOverflow,
  });

  // Grid element used to scope column content measurement to this table (excluding nested subtables).
  const gridRef = useRef<HTMLDivElement>(null);
  // Which column's header options menu is open (only one at a time).
  const [openMenuColId, setOpenMenuColId] = useState<string | null>(null);
  // Column options (sorting + width) live behind the header caret, which requires managed widths.
  const showColumnMenu = enableColumnResizing && !!tableWidth;

  // Measures how many extra pixels a single column needs to fully reveal its (currently clipped)
  // content: the max content delta across the column's cells, plus a small buffer. Only rendered rows
  // are measured, so with pagination this fits the current page; a wider value on another page can
  // still clip. Scoped to this grid's cells, skipping any nested subtable's.
  const measureColumnContentDelta = useCallback((colId: string): number => {
    const grid = gridRef.current;
    if (!grid) {
      return 0;
    }
    let maxDelta = 0;
    grid.querySelectorAll<HTMLElement>(`[data-column-id="${CSS.escape(colId)}"]`).forEach((cell) => {
      if (cell.closest('[role="grid"]') !== grid) {
        return;
      }
      maxDelta = Math.max(maxDelta, measureCellContentDelta(cell));
    });
    return maxDelta > 0 ? maxDelta + MAXIMIZE_COLUMN_BUFFER : 0;
  }, []);

  // Measures every visible column at once (for "maximize all columns" in overflow mode) in a single
  // grid walk, bucketing each cell's delta by its column id so every cell is visited only once.
  const measureColumnContentDeltas = useCallback((): Record<string, number> => {
    const grid = gridRef.current;
    if (!grid) {
      return {};
    }
    const maxDeltaByColId: Record<string, number> = {};
    grid.querySelectorAll<HTMLElement>("[data-column-id]").forEach((cell) => {
      const colId = cell.dataset.columnId;
      // Skip cells belonging to a nested subtable inside this grid.
      if (colId === undefined || cell.closest('[role="grid"]') !== grid) {
        return;
      }
      maxDeltaByColId[colId] = Math.max(maxDeltaByColId[colId] ?? 0, measureCellContentDelta(cell));
    });
    const result: Record<string, number> = {};
    for (const colId of visibleColumnIds) {
      const maxDelta = maxDeltaByColId[colId] ?? 0;
      if (maxDelta > 0) {
        result[colId] = maxDelta + MAXIMIZE_COLUMN_BUFFER;
      }
    }
    return result;
  }, [visibleColumnIds]);

  // From https://tanstack.com/table/v8/docs/framework/react/examples/editable-data
  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();

  // Column filters and global filter (search) cannot co-exist as properties. Causes errors.
  // TODO: This is not an ideal implementation, as it will disable a column filter when searching,
  // however, column filters are not widely implemented, and so am leaving this in here to
  // prevent a future implementation that might hit the same issue and cause errors.
  // If we use the DataTable's columnFilters we'll need to figure out how to use alongside global search.
  const filterState =
    deferredFilter || !columnFilters?.length
      ? {
          globalFilter: deferredFilter,
        }
      : {
          columnFilters,
        };

  const table = useReactTable<T>({
    data: records,
    columns: useMemo(
      () => generateColDefs(columns, tableWidth, columnVisibility),
      [columns, tableWidth, columnVisibility],
    ),
    state: {
      sorting: internalSortingState,
      rowSelection: externalRowSelection ?? rowSelectionInternal,
      columnVisibility,
      columnSizing,
      ...filterState,
      pagination: externalPagination ?? paginationInternal,
      grouping: hasGrouping ? grouping : undefined,
      expanded: externalExpanded ?? expandedInternal,
    },
    getRowId: (originalRow, i) => String(getRowId?.(originalRow) ?? originalRow[rowIdColumn ?? ("id" as keyof T)] ?? i),
    onExpandedChange: setExternalExpanded ?? setExpandedInternal,
    manualPagination: !!setExternalPagination,
    enableRowSelection,
    enableMultiRowSelection: !!multiSelect,
    onRowSelectionChange: setExternalRowSelection ?? setRowSelectionInternal,
    onSortingChange: handleSortingChange,
    onColumnSizingChange: handleColumnSizingChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel:
      externalPagination?.pageSize || paginationInternal?.pageSize ? getPaginationRowModel() : undefined,
    getSortedRowModel: !useServerSideSorting ? getSortedRowModel() : undefined,
    manualSorting: useServerSideSorting,
    getExpandedRowModel: !!expandable || hasGrouping ? getExpandedRowModel() : undefined,
    getGroupedRowModel: hasGrouping ? getGroupedRowModel() : undefined,
    getRowCanExpand: getRowCanExpand ?? (!!expandable ? () => true : undefined),
    onColumnVisibilityChange: setColumnVisibility,
    renderFallbackValue: FALLBACK_VALUE,
    enableColumnResizing,
    columnResizeMode: "onChange",
    autoResetPageIndex: autoResetPageIndexProp && autoResetPageIndex,
    // There are much more advanced filtering options we can explore as needed: https://tanstack.com/table/v8/docs/guide/filters
    globalFilterFn: "includesString",
    meta: {
      // Add parent data to the table meta (for subTables).
      parent,
      isMobileView,
      onEdit,
      onAdd,
      updateCell: (rowIndex, columnId, value) => {
        // Skip page index reset until after next rerender
        skipAutoResetPageIndex();
        setRecords((old) =>
          old.map((row, index) => {
            if (index === rowIndex) {
              return {
                ...old[rowIndex],
                [columnId]: value,
              };
            }
            return row;
          }),
        );
      },
      emptyRow: controls?.addNew?.emptyRow,
      getRowId: getRowId ?? ((row: T) => row[rowIdColumn ?? ("id" as keyof T)] as string | number),
      rowIdColumn: rowIdColumn ?? "id",
    },
  });

  tableRef.current = table;

  const getTableData = useMemo(
    () => ({
      getColumnHeaders: () =>
        table
          .getVisibleFlatColumns()
          .map(
            (column) => ((column.columnDef as DataTableColumn<T>).exportHeader ?? column.columnDef.header) as string,
          ),
      // Returns an array of rows' values (for CSV export)
      // using getValue instead of renderValue to get raw values
      // must be able to use same exported CSV as an import file
      getProcessedRowValues: () =>
        (() => {
          if (table.getIsSomeRowsSelected()) {
            return table.getSelectedRowModel();
          }
          return table.getFilteredRowModel();
        })().rows.map((row) => {
          return row.getVisibleCells().map((cell) => {
            const value = cell.getValue<string | number | boolean>();
            if (typeof value === "object") {
              return JSON.stringify(value);
            }
            return value;
          });
        }),
      // Returns the IDs of visible columns which could be
      // the 'field' or 'id' value provided during column def
      getColumnIds: () =>
        table
          .getVisibleFlatColumns()
          .map((col) => col.id)
          .filter(Boolean),
    }),
    [table],
  );

  useEffect(() => {
    getTableAPI?.(getTableData);
  }, [table, getTableAPI, getTableData]);

  // Reset filters if columns change
  // Effectively this reapplies the filters based on available data
  useEffect(() => {
    table.resetGlobalFilter();
    table.resetColumnFilters();
  }, [table, columns]);

  const tableRows = table.getRowModel().rows;

  const moveRow = draggable
    ? (dragIndex: number, hoverIndex: number): void => {
        setRecords((prev) => {
          const currentRecord = prev[dragIndex];

          const updatedRecords = [...prev];
          updatedRecords.splice(dragIndex, 1);
          updatedRecords.splice(hoverIndex, 0, currentRecord);

          if (onRowMove) {
            onRowMove(updatedRecords, dragIndex);
          }

          return updatedRecords;
        });
      }
    : undefined;

  const dropRow = draggable
    ? (dragIndex: number) => {
        if (onRowDrop) {
          onRowDrop(records, dragIndex);
        }
      }
    : undefined;

  const shiftMultiselect = multiSelect
    ? (row: Row<T>, shiftKey: boolean) => {
        const lastSelectedRow = refState.current.lastSelectedRow;

        if (shiftKey) {
          if (typeof lastSelectedRow === "number" && !isNaN(lastSelectedRow)) {
            const start = Math.min(lastSelectedRow, row.index);
            const end = Math.max(lastSelectedRow, row.index);
            for (let i = start; i <= end; i++) {
              tableRows[i].toggleSelected(true);
            }
          }
        }

        if (!shiftKey && !row.getIsSelected()) {
          refState.current.lastSelectedRow = row.index;
        }
      }
    : null;

  const isPaginationLoading = useMemo(() => {
    return externalPagination?.isLoading === true || paginationInternal?.isLoading === true;
  }, [externalPagination?.isLoading, paginationInternal?.isLoading]);

  const shouldShowPagination = useMemo(() => {
    return (
      (paginationMethod === "cursor" && shouldShowCursorPagination(externalPagination)) ||
      !!(externalPagination?.pageSize && (totalCount ?? 0) > externalPagination?.pageSize) ||
      !!(paginationInternal?.pageSize && records?.length > paginationInternal?.pageSize) ||
      isPaginationLoading
    );
  }, [paginationMethod, externalPagination, paginationInternal, totalCount, records, isPaginationLoading]);

  const showFooter =
    table
      .getFooterGroups()[0]
      .headers.map((header) => header.column.columnDef.footer)
      .filter(Boolean).length > 0;

  // Determine what to render based on current state
  const hasRawData = (data?.length ?? 0) > 0;
  const hasRows = tableRows?.length > 0;
  const isFiltered = hasRawData && !hasRows;

  let emptyState: ReactNode = null;
  if (loading) {
    emptyState = <DataTableLoading />;
  } else if (isFiltered) {
    emptyState = <EmptySearchResults />;
  } else if (emptyCTA) {
    emptyState = (
      <div className={cx(styles.emptyCTA, emptyCTAContainerClassName)}>
        <div className={styles.emptyContent}>{emptyCTA}</div>
      </div>
    );
  }

  const showTable = hasRows || !emptyState;

  return (
    <div
      {...props}
      className={cx(
        styles.wrapper,
        styles[align],
        styles[size],
        { styles, rowGaps, cellBorders, zebra },
        controls && styles.hasControls,
        isReady && styles.isReady,
        loading && !!data && styles.reloading,
        className,
      )}
      ref={ref}
    >
      {controls && (
        <Controls
          {...controls}
          addNewRow={addNewRow}
          // disables search if no data
          // use external searchValue and setSearch (eg. from paginated fetch)
          setGlobalFilter={controls.setSearch || setGlobalFilter}
          globalFilter={controls.searchValue ?? globalFilter}
          getTableData={getTableData}
          tableRef={ref}
        />
      )}
      {tableInfo && <div className={styles.tableInfo}>{tableInfo}</div>}
      {multiSelect && (
        <MultiSelectMenu
          table={table}
          multiSelect={multiSelect}
          rowSelection={rowSelectionInternal}
          containerRef={setMultiSelectNode}
          hasControls={!!controls}
        />
      )}
      {showTable ? (
        <div className={horizontalOverflow ? styles.horizontalOverflowWrapper : undefined}>
          <div className={cx(horizontalOverflow && styles.horizontalOverflow, styles.table)} role="grid" ref={gridRef}>
            {showColumnHeaders && (
              <>
                {table.getHeaderGroups().map((hg, hgIndex) => (
                  <div
                    className={cx(styles.rowWrapper, {
                      styles,
                      fixedHeader,
                      hasControls: !!controls,
                      hasMultiSelect: !!multiSelect,
                    })}
                    key={hgIndex}
                    role="rowgroup"
                  >
                    {multiSelect && (
                      <MultiSelectHeader
                        table={table}
                        rowSelection={rowSelectionInternal}
                        totalCount={totalCount ?? records?.length ?? 0}
                        currentCount={records?.length ?? 0}
                      />
                    )}
                    <div className={styles.header}>
                      {draggable && <div className={styles.dummyCell} />}
                      {!!expandable && <div className={cx(styles.dummyCell, styles.expandCell)} />}
                      {hg.headers
                        .filter((header) => header.column.getIsVisible())
                        .map((header, visibleColIndex) => {
                          // Disable sorting when drag enabled
                          const sortEnabled = header.column.columnDef.enableSorting; // can be undefined
                          const sortable = sortEnabled !== false && !draggable && header.column.getCanSort();
                          const style: HTMLAttributes<HTMLDivElement>["style"] = {};

                          if (tableWidth) {
                            style.width = header.getSize();
                          }
                          const canResize = header.column.getCanResize();
                          const lastCol = visibleColIndex + 1 === visibleColumnCount;
                          const resizeHandler = canResize ? header.getResizeHandler() : undefined;
                          const columnName = flexRender(header.column.columnDef.header, header.getContext());
                          const columnDefTyped = header.column.columnDef as DataTableColumn<T>;
                          const headerTooltip = columnDefTyped.headerTooltip;

                          return (
                            <div
                              key={header.id}
                              className={cx(
                                styles.cell,
                                sortable && styles.sortable,
                                columnLeadSeparatorIds.has(header.column.id) && styles.columnLeadSeparator,
                              )}
                              // using onMouseDown to prevent getting called by resizeHandler
                              onMouseDown={sortable ? header.column.getToggleSortingHandler() : undefined}
                              style={style}
                              role="columnheader"
                              aria-label={columnDefTyped.ariaLabel}
                              data-column-id={header.column.id}
                              title={!headerTooltip && typeof columnName === "string" ? columnName : undefined}
                            >
                              <div className={cx(styles.cellContents, sortable && styles.sortIcon)}>
                                {columnName}
                                {headerTooltip &&
                                  wrapWithTooltip(
                                    <IconInfoLine size={15} className={styles.headerTooltipIcon} />,
                                    headerTooltip,
                                  )}
                                {sortable && <SortByIndicator header={header} />}
                              </div>
                              {(!lastCol || horizontalOverflow) && canResize && (
                                <div
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    resizeHandler?.(e);
                                  }}
                                  onTouchStart={(e) => {
                                    e.stopPropagation();
                                    resizeHandler?.(e);
                                  }}
                                  className={cx(styles.resizer, header.column.getIsResizing() && styles.isResizing)}
                                />
                              )}
                              {showColumnMenu && (
                                <ColumnHeaderMenu
                                  open={openMenuColId === header.column.id}
                                  onOpenChange={(open) => setOpenMenuColId(open ? header.column.id : null)}
                                  columnLabel={typeof columnName === "string" ? columnName : columnDefTyped.ariaLabel}
                                  canSort={sortable}
                                  sortDirection={header.column.getIsSorted()}
                                  onSortAsc={() => header.column.toggleSorting(false)}
                                  onSortDesc={() => header.column.toggleSorting(true)}
                                  onClearSort={() => header.column.clearSorting()}
                                  canMaximize={canResize}
                                  onMaximizeColumn={() =>
                                    maximizeColumn(header.column.id, measureColumnContentDelta(header.column.id))
                                  }
                                  onMaximizeAllColumns={
                                    horizontalOverflow ? () => maximizeColumns(measureColumnContentDeltas()) : undefined
                                  }
                                />
                              )}
                            </div>
                          );
                        })}

                      {!!overflowMenu && !showHideColumns && <div className={styles.overflowCell} />}
                      {showHideColumns && (
                        <div className={cx(styles.overflowCell, styles.rightAligned)} role="gridcell">
                          <ShowHideColumns columns={table.getAllColumns()} storageKey={showHideColumnsStorageKey} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
            <div className={styles.body} role="rowgroup">
              {tableRows.map((row, rowIndex) => {
                return (
                  <TableRow
                    key={row.id}
                    tableRef={ref?.current}
                    row={row}
                    index={rowIndex}
                    moveRow={moveRow}
                    dropRow={dropRow}
                    onRowClick={getRowCanClick(row) ? onRowClick : undefined}
                    onRowMouseEnter={onRowMouseEnter}
                    onRowMouseLeave={onRowMouseLeave}
                    getRowClassNames={getRowClassNames}
                    isRowSelected={isRowSelected}
                    isRowHighlighted={isRowHighlighted}
                    draggable={draggable}
                    getRowHref={getRowHref}
                    rowStateClassNames={rowStateClassNames}
                    expandableRow={expandableRow}
                    subTable={subTable}
                    parentTableWidth={tableWidth ?? undefined}
                    overflowMenu={overflowMenu}
                    scrollIntoView={scrollIntoView}
                    shiftMultiselect={shiftMultiselect}
                  />
                );
              })}
              {showFooter &&
                table.getFooterGroups().map((hg, hgIndex) => (
                  <div
                    className={cx(styles.rowWrapper, {
                      styles,
                      fixedHeader: fixedFooter,
                      fixedHeaderBottom: fixedFooter,
                      shouldShowPagination: fixedFooter && shouldShowPagination && stickyPagination,
                    })}
                    key={hgIndex}
                    role="rowgroup"
                  >
                    <div className={styles.header}>
                      {draggable && <div className={styles.dummyCell} />}
                      {!!expandable && <div className={styles.dummyCell} />}
                      {hg.headers
                        .filter((header) => header.column.getIsVisible())
                        .map((header, visibleColIndex) => {
                          const sortable = false;
                          const style: HTMLAttributes<HTMLDivElement>["style"] = {};

                          if (tableWidth) {
                            style.width = header.getSize();
                          }
                          const canResize = header.column.getCanResize();
                          const lastCol = visibleColIndex + 1 === visibleColumnCount;
                          const resizeHandler = canResize ? header.getResizeHandler() : undefined;
                          const columnName = flexRender(header.column.columnDef.footer, header.getContext());
                          return (
                            <div
                              key={header.id}
                              className={cx(styles.cell, sortable && styles.sortable)}
                              // using onMouseDown to prevent getting called by resizeHandler
                              onMouseDown={sortable ? header.column.getToggleSortingHandler() : undefined}
                              style={style}
                              role="columnheader"
                              title={typeof columnName === "string" ? columnName : undefined}
                            >
                              <div className={cx(styles.cellContents, sortable && styles.sortIcon)}>
                                {columnName}
                                {sortable && <SortByIndicator header={header} />}
                              </div>
                              {(!lastCol || horizontalOverflow) && canResize && (
                                <div
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    resizeHandler?.(e);
                                  }}
                                  onTouchStart={(e) => {
                                    e.stopPropagation();
                                    resizeHandler?.(e);
                                  }}
                                  className={cx(styles.resizer, header.column.getIsResizing() && styles.isResizing)}
                                />
                              )}
                            </div>
                          );
                        })}

                      {!!overflowMenu && <div className={styles.overflowCell} />}
                    </div>
                  </div>
                ))}
              {shouldShowPagination && (
                <Pagination
                  paginationMethod={paginationMethod}
                  pagination={externalPagination ?? paginationInternal}
                  setPagination={setExternalPagination ?? setPaginationInternal}
                  table={table}
                  tableWidth={ref.current?.clientWidth}
                  totalCount={totalCount}
                  canMultiSelect={!!multiSelect}
                  stickyPagination={stickyPagination}
                  pageSizes={pageSizes}
                  isLoading={isPaginationLoading}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        emptyState
      )}
    </div>
  );
}

function SortByIndicator<T>({ header }: { header: Header<T, unknown> }) {
  const sorted = header.column.getIsSorted();
  let direction: "up" | "down" | undefined;
  if (sorted === "asc") {
    direction = "up";
  } else if (sorted === "desc") {
    direction = "down";
  }
  return <IconSort className={cx(!sorted && styles.hidden)} direction={direction} />;
}

function useSkipper() {
  const shouldSkipRef = useRef(true);
  const shouldSkip = shouldSkipRef.current;

  // Wrap a function with this to skip a pagination reset temporarily
  const skip = useCallback(() => {
    shouldSkipRef.current = false;
  }, []);

  useEffect(() => {
    shouldSkipRef.current = true;
  });

  return [shouldSkip, skip] as const;
}

/** Preserve user visibility selections, apply defaults for new columns, drop stale keys. */
export function computeColumnVisibility<T extends {}>(
  columns: DataTableColumn<T>[],
  prev: Record<string, boolean>,
): Record<string, boolean> {
  const defaults = Object.fromEntries(columns.map((c) => [getColumnId(c), !c.hide]));
  return Object.fromEntries(Object.keys(defaults).map((id) => [id, id in prev ? prev[id] : defaults[id]]));
}

/** Styled tables */
export function CatalogTable<T extends {}>({ className, ...props }: DataTableProps<T>) {
  return (
    <DataTable
      size="small"
      align="left"
      fadeIn
      cellBorders={false}
      className={cx(styles.catalogTable, className)}
      {...props}
    />
  );
}

export function ConfigTable<T extends {}>({ className, ...props }: DataTableProps<T>) {
  return (
    <DataTable size="large" align="left" cellBorders={false} className={cx(styles.configTable, className)} {...props} />
  );
}

export function ProcureTable<T extends {}>({ className, ...props }: DataTableProps<T>) {
  return (
    <DataTable
      size="small"
      align="left"
      fadeIn
      cellBorders={false}
      className={cx(styles.stoneLightX5Chrome, className)}
      {...props}
    />
  );
}

export function StudioTable<T extends {}>({ className, cellBorders = true, ...props }: DataTableProps<T>) {
  return (
    <DataTable
      size="xsmall"
      align="left"
      cellBorders={cellBorders}
      className={cx(styles.stoneLightX5Chrome, className)}
      getRowClassNames={() => styles.studioTableRow}
      {...props}
    />
  );
}

export function StudioTableDeleteRowIcon({ onClick }: { onClick: () => void }) {
  return <IconTrash className={styles.studioTableDeleteRowIcon} onClick={onClick} />;
}
